import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';

const SITE_NAME = "Awaken India Tours";
const SITE_URL = "https://awakenindiatours.com";
const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1564507592333-c60657eea523?w=1200&h=630&fit=crop";

// Long-tail keywords for SEO
const KEYWORDS = {
  primary: [
    "luxury India tour Taj Mahal and Himalayas",
    "small group Golden Triangle India tour",
    "Delhi Agra Jaipur Rishikesh tour",
    "first time India tour package",
    "unforgettable first India trip"
  ],
  secondary: [
    "luxury small group India travel",
    "premium India tour experience",
    "curated India journey",
    "boutique India travel",
    "exclusive India tour",
    "India palace tour",
    "Rajasthan luxury tour",
    "spiritual India journey",
    "Himalayan wellness retreat",
    "India cultural immersion tour"
  ]
};

export default function SEO({ 
  title, 
  description, 
  keywords = [], 
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  schema
}) {
  const pageTitle = title 
    ? title + " | " + SITE_NAME 
    : SITE_NAME + " | Luxury Small-Group Journeys to India";
  const fullUrl = url ? SITE_URL + url : SITE_URL;
  const allKeywords = [...KEYWORDS.primary, ...KEYWORDS.secondary, ...keywords].join(", ");
  const metaDescription = description || "Experience an unforgettable first India trip with our luxury small group tour.";

  // Set document title directly to avoid Helmet issues
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <Helmet>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords} />
      <meta name="author" content="Awaken India Tours" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={fullUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Awaken India Tours" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

// Pre-defined schemas for different page types
export const schemas = {
  organization: {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "Awaken India Tours",
    "description": "Luxury small-group journeys to India featuring Taj Mahal, royal palaces, and Himalayan retreats. Designed for first-time India travelers seeking comfort without chaos.",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo.png`,
    "image": DEFAULT_IMAGE,
    "telephone": "+1-781-367-4010",
    "email": "aaron@awakenindiatours.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "priceRange": "$$$",
    "sameAs": []
  },

  product: {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "10-Day Luxury India Tour: Taj, Palaces & Himalayas",
    "description": "An unforgettable first India trip featuring the Golden Triangle (Delhi, Agra, Jaipur) plus Rishikesh in the Himalayas. Small group of 6-8 guests with luxury accommodations.",
    "brand": {
      "@type": "Brand",
      "name": "Awaken India Tours"
    },
    "offers": {
      "@type": "Offer",
      "price": "6950",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "validFrom": "2025-01-01",
      "priceValidUntil": "2026-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "12"
    }
  },

  faq: (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }),

  breadcrumb: (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.url}`
    }))
  })
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: null, // Uses default
    description: "Experience an unforgettable first India trip with our luxury small group tour. Visit the Taj Mahal, royal palaces of Rajasthan, and serene Himalayan retreats. The perfect Delhi Agra Jaipur Rishikesh tour for first-time India travelers.",
    keywords: ["10-day India tour", "luxury India vacation", "best India tour for Americans"],
    url: "/"
  },
  journey: {
    title: "The Journey - 10 Day Luxury India Itinerary",
    description: "Discover our small group Golden Triangle India tour featuring Delhi, Agra's Taj Mahal, Jaipur's palaces, and Rishikesh's Himalayan serenity. A curated first time India tour package designed for comfort seekers.",
    keywords: ["Golden Triangle tour", "India itinerary", "10 day India trip"],
    url: "/journey"
  },
  itinerary: {
    title: "Day-by-Day Itinerary - Delhi Agra Jaipur Rishikesh Tour",
    description: "Detailed day-by-day itinerary for our luxury India tour Taj Mahal and Himalayas experience. From Delhi's culture to Agra's wonder, Jaipur's grandeur, and Rishikesh's peace.",
    keywords: ["India tour itinerary", "day by day India tour", "Golden Triangle itinerary"],
    url: "/itinerary"
  },
  pricing: {
    title: "Pricing & Dates - First Time India Tour Package",
    description: "Investment details for our luxury small group India tour. From $6,950 per person for an unforgettable first India trip. Includes 5-star hotels, private transport, and curated experiences.",
    keywords: ["India tour cost", "luxury India tour price", "India tour dates 2026"],
    url: "/pricing"
  },
  about: {
    title: "About Your Host - Aaron's India Journey",
    description: "Meet Aaron, your guide for this small group Golden Triangle India tour. With deep India expertise and a passion for transformative travel, he crafts unforgettable first India trips.",
    keywords: ["India tour guide", "India travel expert", "guided India tour"],
    url: "/about"
  },
  faqs: {
    title: "FAQs - First Time India Travel Questions Answered",
    description: "Common questions about our luxury India tour Taj Mahal and Himalayas journey. Visa, safety, what to pack, and everything first-time India travelers need to know.",
    keywords: ["India travel FAQ", "first time India questions", "India tour questions"],
    url: "/faqs"
  },
  howItWorks: {
    title: "How It Works - Book Your Luxury India Tour",
    description: "Simple 5-step process to reserve your spot on our small group Golden Triangle India tour. From inquiry to departure, we handle every detail of your first time India tour package.",
    keywords: ["book India tour", "India tour booking process", "reserve India trip"],
    url: "/how-it-works"
  },
  travelPrep: {
    title: "Travel Prep - Essential India Travel Guide",
    description: "Everything you need for your Delhi Agra Jaipur Rishikesh tour. Visa guidance, packing tips, health advice, and cultural insights for an unforgettable first India trip.",
    keywords: ["India travel tips", "India visa guide", "what to pack for India"],
    url: "/travel-prep"
  },
  contact: {
    title: "Contact & Inquire - Start Your India Journey",
    description: "Ready for your luxury India tour Taj Mahal and Himalayas adventure? Contact us to learn more about our first time India tour package and reserve your spot.",
    keywords: ["contact India tour", "India tour inquiry", "book India trip"],
    url: "/contact"
  },
  testimonials: {
    title: "Testimonials - Guest Reviews of Our India Tour",
    description: "Read what past travelers say about their unforgettable first India trip with Awaken India Tours. Real reviews from our small group Golden Triangle India tour guests.",
    keywords: ["India tour reviews", "India travel testimonials", "luxury India tour reviews"],
    url: "/testimonials"
  },
  privacyPolicy: {
    title: "Privacy Policy",
    description: "Privacy policy for Awaken India Tours. Learn how we protect your personal information when you inquire about our luxury India tour packages.",
    keywords: [],
    url: "/privacy-policy"
  },
  termsOfService: {
    title: "Terms of Service",
    description: "Terms and conditions for booking with Awaken India Tours. Cancellation policy, traveler responsibilities, and booking terms for our India tours.",
    keywords: [],
    url: "/terms-of-service"
  },
  firstTrip: {
    title: "First Trip to India - 10 Day Small Group Journey",
    description: "Taj Mahal, Jaipur's palaces, and the Himalayas – designed for 40-65 year olds visiting India for the first time. Small group luxury tour with 5-star hotels and personal hosting.",
    keywords: ["first trip to India", "India tour for beginners", "luxury India tour for seniors", "small group India tour 40-65", "first time India travel"],
    url: "/first-trip-to-india"
  }
};
